import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import cx from 'classnames'

import { GAME_TYPE } from 'lib/constants'
import Button from 'components/Button'
import s from './landing.module.css'

const StartGameButton = ({ game }: { game: GAME_TYPE }) => {
  const { color } = game
  return (
    <Link href={`https://mcp.codingfantasy.com`} passHref>
      <Button tag="a" btnColor={`bg-${color}-600`} btnHoverColor={`bg-${color}-700`}>
        Start Game & Learn MCP →
      </Button>
    </Link>
  )
}

const Landing = ({ game }: { game: GAME_TYPE }) => {
  return (
    <>
      <div className={cx('bg-black w-full h-full text-white py-16 min-h-screen px-4 relative')}>
        <div className={s.heroBackground}></div>
        <div className={cx('flex flex-col items-center z-10 relative')}>
          <div className="relative h-96 w-full mb-6">
            <Image
              src={'/images/mcp/all-empty.png'}
              layout="fill"
              quality={100}
              objectFit="contain"
              alt="MCP Panic Game"
            />
          </div>
          <div className="w-full">
            <h1
              className={cx(
                s.highlightTitle,
                'mb-8 mt-8 lg:text-7xl text-5xl font-bold text-center text-red-500'
              )}
            >
              Model Context
              <br />
              Protocol Panic
            </h1>
            <h2 className="text-center text-2xl font-bold text-gray-200">
              Chaos. Code. Coffee. And a Ticking Clock.
            </h2>
            <div className="lg:max-w-xl max-w-md m-auto text-gray-200 mt-12">
              <p className="mb-6 lg:text-2xl md:text-xl text-lg">
                48 hours until TechCrunch Demo Day.
              </p>
              <p className="mb-6 lg:text-2xl md:text-xl text-lg">
                The CTO is missing (last seen at Burning Man)
              </p>
              <p className="mb-6 lg:text-2xl md:text-xl text-lg">
                The entire engineering team quit (and went viral on Twitter)
              </p>
              <p className="mb-6 lg:text-2xl md:text-xl text-lg">
                Company apocalyptic prediction AI just relocated itself as it forecasts a market
                collapse.
              </p>
              <p className="mb-6 lg:text-2xl md:text-xl text-lg">
                And your CEO thinks "quantum blockchain NFT consciousness" is a viable pivot
                strategy.
              </p>
              <p className="mb-6 lg:text-2xl md:text-xl text-lg">
                Welcome to Disruptor.AI, where your first day as lead engineer might also be your
                last!
              </p>
            </div>

            <div className="w-full flex justify-center mt-12">
              <StartGameButton game={game} />
            </div>

            <div className="lg:max-w-xl max-w-md m-auto text-gray-200 mt-12">
              <h3 className="text-2xl font-bold mb-6 text-red-500">Your Mission</h3>
              <p className="mb-6 lg:text-xl md:text-lg text-base">
                Master Antropic's{' '}
                <a
                  href="https://modelcontextprotocol.io/introduction?ref=codingfantasy.com/games/mcp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Model Context Protocol
                </a>{' '}
                and rebuild a functional AI prediction system before Silicon Valley's latest
                "unicorn" reveals itself to be a particularly flammable horse in disguise. Work with
                Steve, the cynical senior developer on his last day, to decode the cryptic fragments
                of "Cassandra" – an AI system that seems to be predicting the future with to be
                predicting the future with unsettling accuracy.
              </p>

              <h3 className="text-2xl font-bold mb-6 text-red-500">Key Features</h3>
              <ul className="list-disc pl-6 space-y-4 lg:text-xl md:text-lg text-base">
                <li>
                  <span className="font-bold">Immersive Silicon Valley Satire:</span> Experience
                  startup chaos complete with a Stanford dropout CEO, venture capital buzzword
                  bingo, and literal fires in the HR department.
                </li>
                <li>
                  <span className="font-bold">Technical Challenges:</span> Implement real MCP
                  components including Resources, Prompts, and Tools capabilities that mirror
                  genuine AI integration protocols.
                </li>
                <li>
                  <span className="font-bold">Compelling Narrative:</span> Unravel the mystery of
                  Project Cassandra and its missing creator as you race against time to prepare for
                  TechCrunch Disrupt.
                </li>
                <li>
                  <span className="font-bold">Ethical Dilemmas:</span> When you discover an AI that
                  can predict (or perhaps influence) market movements, do you expose the truth or
                  protect those who might profit?
                </li>
                <li>
                  <span className="font-bold">Code & Comedy:</span> Debug your way through a story
                  where the only documentation is written in sarcasm and despair.
                </li>
              </ul>

              <h3 className="text-2xl font-bold mb-6 mt-8 text-red-500">Tech Stack</h3>
              <p className="mb-6 lg:text-xl md:text-lg text-base">
                Build a working implementation using JavaScript and the Model Context Protocol SDK.
                Connect to mysterious servers, integrate resources, implement standardized prompts,
                and create executable tools – all while your CEO pitches "quantum neural blockchain
                interfaces" to increasingly skeptical investors.
              </p>
            </div>

            <div className="w-full flex justify-center mt-12">
              <StartGameButton game={game} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Landing
